import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { PieChartLegend, PIE_CHART_MARGIN } from '../charts/pieChartLegend';
import RequiredApiNotice from './RequiredApiNotice';

interface EconomyDashboardProps {
  data: any;
  address: any;
}

const COLORS = ['#0ea5e9', '#22c55e', '#f97316', '#6366f1', '#e11d48', '#a855f7', '#14b8a6'];

const splitEducationHealthcareIndustries = (industries: any[] = []) => {
  const expanded: any[] = [];
  for (const entry of industries) {
    const name = String(entry?.name || '');
    const share = Number(entry?.share ?? entry?.value);
    if (/education\s*&\s*healthcare/i.test(name) && Number.isFinite(share) && share > 0) {
      const educationShare = Math.round(share * 0.43 * 10) / 10;
      const healthcareShare = Math.round((share - educationShare) * 10) / 10;
      expanded.push({ ...entry, name: 'Education', share: educationShare, value: educationShare });
      expanded.push({ ...entry, name: 'Healthcare', share: healthcareShare, value: healthcareShare });
      continue;
    }
    expanded.push(entry);
  }
  return expanded;
};

const EconomyDashboard: React.FC<EconomyDashboardProps> = ({ data, address }) => {
  if (!data || data.error) {
    return (
      <div>
        <div>Data not available</div>
        <RequiredApiNotice apis={['US Census ACS 5-Year API', 'Census DP03 Profile API', 'BLS QCEW API']} />
      </div>
    );
  }

  const missingFields: string[] = [];
  if (!data.unemploymentRate) missingFields.push('Unemployment Rate');
  if (!data.medianHouseholdIncome) missingFields.push('Median Household Income');
  if (!data.industries || data.industries.length === 0) missingFields.push('Industry Distribution');

  const metroIndustries = splitEducationHealthcareIndustries(data.metroIndustries || data.industries || []);

  // Static neighborhood breakdown based on PDF report template.
  const defaultNeighborhoodIndustries = [
    { name: 'Education', value: 13 },
    { name: 'Healthcare', value: 9 },
    { name: 'Professional & Scientific', value: 18 },
    { name: 'Manufacturing', value: 12 },
    { name: 'Retail Trade', value: 7 },
    { name: 'Arts & Entertainment', value: 9 },
    { name: 'Finance & Real Estate', value: 9 },
    { name: 'Transportation & Utilities', value: 5 },
    { name: 'Other Services', value: 7 },
    { name: 'Construction', value: 5 },
    { name: 'Public Administration', value: 5 },
  ];

  const neighborhoodIndustries = splitEducationHealthcareIndustries(
    data.neighborhoodIndustries || defaultNeighborhoodIndustries
  );

  const formatPercent = (value: any) => {
    if (value === null || value === undefined) return 'N/A';
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (Number.isNaN(num)) return 'N/A';
    return `${num.toFixed(1)}%`;
  };

  const totalCommuters = data.totalCommuters || null;

  return (
    <div>
      <h2>E - ECONOMY (Economic Diversity)</h2>
      <p style={{ color: '#666', marginBottom: '30px' }}>
        Is the local economy resilient or dependent on one industry?
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '30px',
        }}
      >
        <div className="card" style={{ padding: '20px' }}>
          <h3>Employment Diversity</h3>
          <p style={{ fontSize: '24px', fontWeight: 'bold' }}>{data.employmentDiversity ?? 'N/A'}</p>
        </div>
        <div className="card" style={{ padding: '20px' }}>
          <h3>Unemployment Rate</h3>
          <p style={{ fontSize: '24px', fontWeight: 'bold' }}>
            {data.unemploymentRate != null ? `${data.unemploymentRate}%` : 'N/A'}
          </p>
        </div>
        <div className="card" style={{ padding: '20px' }}>
          <h3>Economic Resilience Score</h3>
          <p style={{ fontSize: '24px', fontWeight: 'bold' }}>{data.economicResilienceScore ?? 'N/A'}</p>
        </div>
      </div>

      {metroIndustries.length > 0 && (
        <div className="card">
          <h3>Metro area vs neighborhood industry mix</h3>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '32px',
              marginTop: '16px',
              alignItems: 'stretch',
            }}
          >
            {/* Metro area chart + table */}
            <div style={{ flex: '1 1 260px', minWidth: 260 }}>
              <h4 style={{ marginBottom: 8 }}>Metro area</h4>
              <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={PIE_CHART_MARGIN}>
                    <Pie
                      data={metroIndustries}
                      cx="50%"
                      cy="45%"
                      outerRadius={72}
                      innerRadius={40}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                    >
                      {metroIndustries.map((entry: any, index: number) => (
                        <Cell key={`metro-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <PieChartLegend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  marginTop: 12,
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ width: 24 }} />
                    <th style={{ textAlign: 'left', padding: '6px 4px' }}>Industry</th>
                    <th style={{ textAlign: 'right', padding: '6px 4px' }}>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {metroIndustries.map((entry: any, index: number) => (
                      <tr key={`metro-row-${index}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '4px 4px' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: COLORS[index % COLORS.length],
                            }}
                          />
                        </td>
                        <td style={{ padding: '4px 4px' }}>{entry.name}</td>
                        <td style={{ padding: '4px 4px', textAlign: 'right' }}>
                          {formatPercent(entry.share ?? entry.value)}
                        </td>
                      </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Neighborhood chart + table */}
            <div style={{ flex: '1 1 260px', minWidth: 260 }}>
              <h4 style={{ marginBottom: 8 }}>Neighborhood</h4>
              {neighborhoodIndustries.length > 0 ? (
                <>
                  <div style={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart margin={PIE_CHART_MARGIN}>
                        <Pie
                          data={neighborhoodIndustries}
                          cx="50%"
                          cy="45%"
                          outerRadius={72}
                          innerRadius={40}
                          paddingAngle={2}
                          dataKey="value"
                          nameKey="name"
                        >
                          {neighborhoodIndustries.map((entry: any, index: number) => (
                            <Cell key={`nbh-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <PieChartLegend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      marginTop: 12,
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <th style={{ width: 24 }} />
                        <th style={{ textAlign: 'left', padding: '6px 4px' }}>Industry</th>
                        <th style={{ textAlign: 'right', padding: '6px 4px' }}>Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {neighborhoodIndustries.map((entry: any, index: number) => (
                        <tr key={`nbh-row-${index}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '4px 4px' }}>
                            <span
                              style={{
                                display: 'inline-block',
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: COLORS[index % COLORS.length],
                              }}
                            />
                          </td>
                          <td style={{ padding: '4px 4px' }}>{entry.name}</td>
                          <td style={{ padding: '4px 4px', textAlign: 'right' }}>
                            {formatPercent(entry.share ?? entry.value)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <p style={{ fontSize: 13, color: '#6b7280', marginTop: 8 }}>
                  Neighborhood industry breakdown not available.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Metro area details card */}
      <div className="card">
        <h3>Metro area details</h3>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: 12,
            fontSize: 13,
          }}
        >
          <tbody>
            <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '6px 4px' }}>Median household income</td>
              <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                {data.medianHouseholdIncome
                  ? `$${data.medianHouseholdIncome.toLocaleString()}`
                  : 'N/A'}
              </td>
            </tr>
            <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '6px 4px' }}>Labor force</td>
              <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                {data.laborForce ? data.laborForce.toLocaleString() : 'N/A'}
              </td>
            </tr>
            <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '6px 4px' }}>Unemployment rate</td>
              <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                {data.unemploymentRate != null ? `${data.unemploymentRate}%` : 'N/A'}
              </td>
            </tr>
            <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '6px 4px' }}>Poverty rate</td>
              <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                {data.povertyRate != null ? `${data.povertyRate}%` : 'N/A'}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '6px 4px' }}>Total commuters</td>
              <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                {totalCommuters ? totalCommuters.toLocaleString() : 'N/A'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {missingFields.length > 0 && (
        <RequiredApiNotice
          apis={['US Census ACS 5-Year API', 'Census DP03 Profile API', 'BLS QCEW API']}
          missingFields={missingFields}
        />
      )}
    </div>
  );
};

export default EconomyDashboard;
